import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useIdempotencyKey } from '@/api/idempotency'
import { useSalesChannels } from '@/api/sales-channels'
import { useChannelStock, useCreateSalesReport } from '@/api/sales-reports'
import type { ChannelStockItem, SalesReportBody } from '@/api/sales-reports'
import type { ApiError } from '@/api/me'
import { useWorkspace } from '@/api/workspace'
import { blockingItemsOf } from '@/components/corrections/blocking-items'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { formatMoney, formatQty } from '@/components/materials/pool-format'
import { formatBusinessDate } from '@/components/opening-balances/format'
import { needsBackdateReason, salesReportErrorMessage } from '@/components/sales-reports/error-message'
import { todayIn } from '@/components/sales-reports/format'
import { SearchCombobox } from '@/components/search-combobox'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'

// Create a Sales Report (UI-SCREENS S-06, 06b #185). Phone first: one card per line, 44px
// targets, numeric keyboards. The Variant picker is limited to what the channel holds right now,
// each line's Vendor Price is prefilled as of the **period end** and flagged when the Owner
// changes it, the expected total adds up live, and the settlement amount starts at the expected
// total — a different one is allowed in either direction with a note (owner decision 2026-09-23,
// Q5). `?channelId=` prefills the channel, so the action on the Sales Channel screen is one tap
// away.
const searchSchema = z.object({
  channelId: z.coerce.number().int().positive().optional(),
})

export const Route = createFileRoute('/w/$slug/sales-reports/new')({
  component: NewSalesReport,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'salesReports.form.title' },
})

function NewSalesReport() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { channelId } = Route.useSearch()
  const workspace = useWorkspace(slug)
  const channels = useSalesChannels(slug, { archived: true })

  if (workspace.isPending || channels.isPending) {
    return <LoadingSkeleton variant="page" />
  }
  if (workspace.isError || !workspace.data || channels.isError || !channels.data) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('errors.generic')}
      </p>
    )
  }

  const adoptedOn = workspace.data.adopted_on
  const options = channels.data.map((channel) => ({ id: channel.id, name: channel.name }))

  return (
    <section className="flex max-w-2xl flex-col gap-4">
      <Link to="/w/$slug/sales-reports" params={{ slug }} className="text-sm underline-offset-4 hover:underline">
        {t('salesReports.back')}
      </Link>
      <h1 className="font-heading text-xl font-semibold">{t('salesReports.form.title')}</h1>

      {!adoptedOn ? (
        <Missing message={t('salesReports.errors.adoptionDateNotSet')} />
      ) : options.length === 0 ? (
        <Missing message={t('salesReports.form.noChannels')} />
      ) : (
        <SalesReportForm
          slug={slug}
          adoptedOn={adoptedOn}
          timezone={workspace.data.timezone}
          channels={options}
          prefilledChannelId={options.some((channel) => channel.id === channelId) ? channelId : undefined}
        />
      )}
    </section>
  )
}

function Missing({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <p role="status" className="text-sm">
          {message}
        </p>
      </CardContent>
    </Card>
  )
}

const EMPTY_LINE = { variantId: '', units: '', vendorPrice: '' }

// Server `details` keys of a 422 validation_failed → the form field and message they show at.
const SERVER_FIELDS: Record<
  string,
  { name: 'channelId' | 'periodStart' | 'periodEnd' | 'settlementAmount' | 'differenceNote'; message: string }
> = {
  sales_channel_id: { name: 'channelId', message: 'salesReports.form.required' },
  period_start: { name: 'periodStart', message: 'salesReports.form.required' },
  period_end: { name: 'periodEnd', message: 'salesReports.form.required' },
  settlement_amount: { name: 'settlementAmount', message: 'salesReports.form.amountInvalid' },
  difference_note: { name: 'differenceNote', message: 'salesReports.form.required' },
}

function SalesReportForm({
  slug,
  adoptedOn,
  timezone,
  channels,
  prefilledChannelId,
}: {
  slug: string
  adoptedOn: string
  timezone: string
  channels: { id: number; name: string }[]
  prefilledChannelId?: number
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const create = useCreateSalesReport()
  const idempotency = useIdempotencyKey()
  // The backdate limit lives on the server; the reason field appears once it asks for one.
  const [askReason, setAskReason] = useState(false)
  const today = todayIn(timezone)

  // Every stored number is an integer (domain-rules); Stock at Channel never goes negative, so
  // units above what the channel holds are refused here too — the server re-checks it under the
  // balance lock. A zero-unit report (no lines at all) is allowed: it closes a cycle.
  const formSchema = z
    .object({
      channelId: z.string().min(1, { message: t('salesReports.form.required') }),
      periodStart: z.string().min(1, { message: t('salesReports.form.required') }),
      periodEnd: z.string().min(1, { message: t('salesReports.form.required') }),
      settlementAmount: z.string(),
      paidInFull: z.boolean(),
      differenceNote: z.string(),
      note: z.string(),
      backdateReason: z.string(),
      lines: z.array(
        z.object({
          variantId: z.string().min(1, { message: t('salesReports.form.required') }),
          units: z.string(),
          vendorPrice: z.string(),
        }),
      ),
    })
    .superRefine((values, context) => {
      if (values.periodEnd < values.periodStart) {
        context.addIssue({ code: 'custom', path: ['periodEnd'], message: t('salesReports.form.periodInvalid') })
      }
      if (values.settlementAmount !== '' && !/^\d+$/.test(values.settlementAmount)) {
        context.addIssue({ code: 'custom', path: ['settlementAmount'], message: t('salesReports.form.amountInvalid') })
      }
      // A settlement that differs from the expected total needs a note (the model rule, #257).
      // `items` is read at submit time, after this render has set it.
      const expectedTotal = expectedOf(values.lines, items)
      if (
        /^\d+$/.test(values.settlementAmount) &&
        Number(values.settlementAmount) !== expectedTotal &&
        values.differenceNote.trim() === ''
      ) {
        context.addIssue({ code: 'custom', path: ['differenceNote'], message: t('salesReports.form.required') })
      }
      values.lines.forEach((line, index) => {
        if (!/^\d+$/.test(line.units) || Number(line.units) < 1) {
          context.addIssue({ code: 'custom', path: ['lines', index, 'units'], message: t('salesReports.form.unitsInvalid') })
        }
        if (line.vendorPrice !== '' && !/^\d+$/.test(line.vendorPrice)) {
          context.addIssue({
            code: 'custom',
            path: ['lines', index, 'vendorPrice'],
            message: t('salesReports.form.amountInvalid'),
          })
        }
      })
    })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      channelId: prefilledChannelId ? String(prefilledChannelId) : String(channels[0].id),
      periodStart: today,
      periodEnd: today,
      settlementAmount: '',
      paidInFull: false,
      differenceNote: '',
      note: '',
      backdateReason: '',
      lines: [],
    },
  })
  const lines = useFieldArray({ control: form.control, name: 'lines' })
  // useWatch rather than form.watch: it returns a value the React compiler can memoize.
  const watched = useWatch({ control: form.control, name: 'lines' })
  const watchedChannel = useWatch({ control: form.control, name: 'channelId' })
  const watchedPeriodEnd = useWatch({ control: form.control, name: 'periodEnd' })
  const watchedSettlement = useWatch({ control: form.control, name: 'settlementAmount' })
  const paidInFull = useWatch({ control: form.control, name: 'paidInFull' })

  // What the channel holds and what each Variant's Vendor Price is as of the chosen period end.
  const stock = useChannelStock(slug, Number(watchedChannel), watchedPeriodEnd, watchedPeriodEnd !== '')
  const items: ChannelStockItem[] = stock.data?.items ?? []

  // The "Harga vendor" value the form itself filled in, per line (by field id). When the period
  // end changes, the stock reloads with prices as of the new date, and every line still holding
  // the value the form filled in is refilled; a price typed by hand stays (#281).
  const filledPrices = useRef<Record<string, string>>({})
  useEffect(() => {
    if (!stock.data) return
    lines.fields.forEach((field, index) => {
      const line = form.getValues(`lines.${index}`)
      if (line.vendorPrice !== filledPrices.current[field.id]) return
      const next = vendorPriceFill(line.variantId, stock.data.items)
      filledPrices.current[field.id] = next
      form.setValue(`lines.${index}.vendorPrice`, next)
    })
  }, [stock.data, lines.fields, form])

  const expected = expectedOf(watched ?? [], items)
  const settlement = /^\d+$/.test(watchedSettlement) ? Number(watchedSettlement) : expected
  const difference = settlement - expected
  const clamped = form.getValues('periodStart') < adoptedOn

  // The first stock item a refused save named: it opens the Adjustment form at that channel
  // (I-11, 05e #173); the rest are named in the message above.
  const blockedItemId = create.isError ? blockingItemsOf(create.error)[0]?.stock_item_id : undefined

  // A 422 validation_failed names the fields in `details`: show them inline (#257).
  const showFieldErrors = (error: unknown) => {
    const body = (error as ApiError | undefined)?.error
    if (body?.key !== 'validation_failed') return
    for (const field of Object.keys((body.details ?? {}) as Record<string, unknown>)) {
      const mapped = SERVER_FIELDS[field]
      if (mapped) form.setError(mapped.name, { message: t(mapped.message) })
    }
  }

  const onSubmit = form.handleSubmit((values) => {
    const backdateReason = values.backdateReason.trim()
    const differenceNote = values.differenceNote.trim()
    const body: SalesReportBody = {
      sales_report: {
        sales_channel_id: Number(values.channelId),
        period_start: values.periodStart,
        period_end: values.periodEnd,
        note: values.note.trim() === '' ? null : values.note.trim(),
        ...(values.settlementAmount === '' ? {} : { settlement_amount: Number(values.settlementAmount) }),
        ...(values.paidInFull ? { paid_in_full: true } : {}),
        ...(differenceNote === '' ? {} : { difference_note: differenceNote }),
        ...(backdateReason === '' ? {} : { backdate_reason: backdateReason }),
        lines: values.lines.map((line) => ({
          variant_id: Number(line.variantId),
          units: Number(line.units),
          ...(line.vendorPrice === '' ? {} : { vendor_price: Number(line.vendorPrice) }),
        })),
      },
    }
    create.mutate(
      { params: { path: { slug }, header: idempotency.headerFor(body) }, body },
      {
        onSuccess: (data) =>
          navigate({ to: '/w/$slug/sales-reports/$reportId', params: { slug, reportId: String(data.id) } }),
        onSettled: (_data, error) => {
          idempotency.settle(error)
          if (needsBackdateReason(error)) setAskReason(true)
          showFieldErrors(error)
        },
      },
    )
  })

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
      <p role="alert" aria-live="assertive" className="text-sm text-destructive empty:hidden">
        {create.isError ? salesReportErrorMessage(t, create.error) : ''}
      </p>
      {blockedItemId ? (
        <Button asChild variant="outline" className="h-11 self-start">
          <Link
            to="/w/$slug/adjustments/new"
            params={{ slug }}
            search={{ location: 'channel', channelId: Number(watchedChannel), itemId: blockedItemId }}
          >
            {t('salesReports.form.toAdjustment')}
          </Link>
        </Button>
      ) : null}

      <div className="flex flex-col gap-2">
        <Label htmlFor="channelId">{t('salesReports.form.channel')}</Label>
        <NativeSelect id="channelId" {...form.register('channelId')}>
          {channels.map((channel) => (
            <option key={channel.id} value={String(channel.id)}>
              {channel.name}
            </option>
          ))}
        </NativeSelect>
        <FieldError message={form.formState.errors.channelId?.message} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="periodStart">{t('salesReports.form.periodStart')}</Label>
          <Input id="periodStart" type="date" className="h-11" {...form.register('periodStart')} />
          <FieldError message={form.formState.errors.periodStart?.message} />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="periodEnd">{t('salesReports.form.periodEnd')}</Label>
          <Input id="periodEnd" type="date" className="h-11" min={adoptedOn} max={today} {...form.register('periodEnd')} />
          <p className="text-sm text-muted-foreground">{t('salesReports.form.periodEndHint')}</p>
          <FieldError message={form.formState.errors.periodEnd?.message} />
        </div>
      </div>
      {clamped ? (
        <p role="status" className="text-sm text-muted-foreground">
          {t('salesReports.form.periodClamped', { date: formatBusinessDate(adoptedOn) })}
        </p>
      ) : null}

      {askReason ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="backdateReason">{t('salesReports.form.backdateReason')}</Label>
          <Input id="backdateReason" className="h-11" {...form.register('backdateReason')} />
          <p className="text-sm text-muted-foreground">{t('salesReports.form.backdateReasonHint')}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {lines.fields.map((field, index) => (
          <Card key={field.id}>
            <CardContent className="flex flex-col gap-3 py-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor={`variant-${index}`}>{t('salesReports.form.variant')}</Label>
                {/* Picking a Variant fills "Harga vendor" with its applied Vendor Price as the
                    value, so saving without editing posts it; the Owner may overwrite it (#262). */}
                <Controller
                  control={form.control}
                  name={`lines.${index}.variantId` as const}
                  render={({ field, fieldState }) => (
                    <SearchCombobox
                      id={`variant-${index}`}
                      value={field.value}
                      options={items.map((item) => ({ value: String(item.variant_id), code: item.sku, name: item.name }))}
                      invalid={Boolean(fieldState.error)}
                      placeholder={t('salesReports.form.selectVariant')}
                      searchPlaceholder={t('searchCombobox.variant.search')}
                      onChange={(value) => {
                        field.onChange(value)
                        const filled = vendorPriceFill(value, items)
                        filledPrices.current[lines.fields[index].id] = filled
                        form.setValue(`lines.${index}.vendorPrice`, filled)
                      }}
                    />
                  )}
                />
                <FieldError message={form.formState.errors.lines?.[index]?.variantId?.message} />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`units-${index}`}>{t('salesReports.form.units')}</Label>
                <Input
                  id={`units-${index}`}
                  inputMode="numeric"
                  className="h-11"
                  {...form.register(`lines.${index}.units`)}
                />
                <p className="text-sm text-muted-foreground">
                  {t('salesReports.form.channelStock', { qty: formatQty(stockOf(watched[index], items)) })}
                </p>
                <FieldError message={form.formState.errors.lines?.[index]?.units?.message} />
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor={`price-${index}`}>{t('salesReports.form.vendorPrice')}</Label>
                <Input
                  id={`price-${index}`}
                  inputMode="numeric"
                  className="h-11"
                  placeholder={String(prefilledOf(watched[index], items))}
                  {...form.register(`lines.${index}.vendorPrice`)}
                />
                <p className="text-sm text-muted-foreground">
                  {t('salesReports.form.prefilledPrice', {
                    price: formatMoney(prefilledOf(watched[index], items)),
                  })}
                  {priceChanged(watched[index], items) ? ` · ${t('salesReports.form.priceChanged')}` : ''}
                </p>
                <FieldError message={form.formState.errors.lines?.[index]?.vendorPrice?.message} />
              </div>

              <Button
                type="button"
                variant="ghost"
                className="h-11 self-start text-destructive"
                onClick={() => lines.remove(index)}
              >
                <Trash2 aria-hidden="true" />
                {t('salesReports.form.removeLine')}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Button type="button" variant="outline" className="h-11 self-start" onClick={() => lines.append(EMPTY_LINE)}>
        {t('salesReports.form.addLine')}
      </Button>
      {lines.fields.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('salesReports.form.zeroUnits')}</p>
      ) : null}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t('salesReports.columns.expected')}</dt>
        <dd className="font-medium">Rp {formatMoney(expected)}</dd>
        <dt className="text-muted-foreground">{t('salesReports.columns.difference')}</dt>
        <dd className="font-medium">{signedMoney(difference)}</dd>
      </dl>

      <div className="flex flex-col gap-2">
        <Label htmlFor="settlementAmount">{t('salesReports.form.settlement')}</Label>
        <Input
          id="settlementAmount"
          inputMode="numeric"
          className="h-11"
          placeholder={String(expected)}
          {...form.register('settlementAmount')}
        />
        <p className="text-sm text-muted-foreground">{t('salesReports.form.settlementHint')}</p>
        <FieldError message={form.formState.errors.settlementAmount?.message} />
      </div>

      {/* "Paid in full" (owner decision 2026-09-23, Q3): offered on every channel, default off.
          Ticked, it records one Payment of the settlement amount on the report's own date. */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <Switch
            id="paidInFull"
            checked={paidInFull}
            onCheckedChange={(next) => form.setValue('paidInFull', next)}
          />
          <Label htmlFor="paidInFull">{t('payments.paidInFull')}</Label>
        </div>
        <p className="text-sm text-muted-foreground">{t('payments.paidInFullHint')}</p>
      </div>

      {difference === 0 ? null : (
        <div className="flex flex-col gap-2">
          <Label htmlFor="differenceNote">{t('salesReports.form.differenceNote')}</Label>
          <Input id="differenceNote" className="h-11" {...form.register('differenceNote')} />
          <p className="text-sm text-muted-foreground">{t('salesReports.form.differenceNoteHint')}</p>
          <FieldError message={form.formState.errors.differenceNote?.message} />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="note">{t('salesReports.form.note')}</Label>
        <Input id="note" className="h-11" {...form.register('note')} />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="h-11" disabled={create.isPending}>
          {t(create.isPending ? 'salesReports.form.saving' : 'salesReports.form.save')}
        </Button>
        <Button asChild variant="ghost" className="h-11">
          <Link to="/w/$slug/sales-reports" params={{ slug }}>
            {t('salesReports.form.cancel')}
          </Link>
        </Button>
      </div>
    </form>
  )
}

type LineValues = { variantId: string; units: string; vendorPrice: string }

const itemOf = (line: LineValues | undefined, items: ChannelStockItem[]) =>
  items.find((item) => String(item.variant_id) === line?.variantId)

// The "Harga vendor" value a picked Variant fills in: its Vendor Price in effect, blank when none.
const vendorPriceFill = (variantId: string, items: ChannelStockItem[]) => {
  const item = items.find((candidate) => String(candidate.variant_id) === variantId)
  return item?.has_vendor_price ? String(item.prefilled_vendor_price) : ''
}

// What the channel holds of the line's Variant right now.
const stockOf = (line: LineValues | undefined, items: ChannelStockItem[]) => itemOf(line, items)?.qty ?? 0

// The Vendor Price in effect on the period end, 0 when the Variant has none (#184 Q3).
const prefilledOf = (line: LineValues | undefined, items: ChannelStockItem[]) =>
  itemOf(line, items)?.prefilled_vendor_price ?? 0

// What the line will be saved at: the typed price, else the prefilled one.
const priceOf = (line: LineValues | undefined, items: ChannelStockItem[]) =>
  line && /^\d+$/.test(line.vendorPrice) ? Number(line.vendorPrice) : prefilledOf(line, items)

// The expected total: each line's units times the price it will be saved at.
const expectedOf = (lines: LineValues[], items: ChannelStockItem[]) =>
  lines.reduce((total, line) => {
    const units = /^\d+$/.test(line.units) ? Number(line.units) : 0
    return total + units * priceOf(line, items)
  }, 0)

// A line whose price the Owner changed is flagged (UI-SCREENS S-06).
const priceChanged = (line: LineValues | undefined, items: ChannelStockItem[]) =>
  priceOf(line, items) !== prefilledOf(line, items)

const signedMoney = (value: number) => `${value > 0 ? '+' : value < 0 ? '−' : ''}Rp ${formatMoney(Math.abs(value))}`

function NativeSelect({ className, ...props }: React.ComponentProps<'select'>) {
  return (
    <select
      className={`h-11 w-full rounded-lg border border-input bg-transparent px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 ${className ?? ''}`}
      {...props}
    />
  )
}

function FieldError({ message }: { message?: string }) {
  return (
    <p role="alert" className="text-sm text-destructive empty:hidden">
      {message ?? ''}
    </p>
  )
}
